import type { EventVault, EventVaultSettings } from "@/models"
import type { IEventVaultSettingsUpdate, IEventVaultSettingsUpdateRequest } from "@/types"
import { useMutation } from "@tanstack/vue-query"
import { axios } from "@/axios"
import { EventWebsiteUrlMode } from "@/models"
import { SocialsBehavior, TaggingBehavior } from "@/types"

export async function updateEventVaultSettings(settings: IEventVaultSettingsUpdate, eventVaultId?: number) {
  if (!eventVaultId) {
    throw new Error(
      "Missing `eventVaultId` when attempting to update Event Vault settings. Please ensure `eventVaultId` is provided.",
    )
  }

  const updatedEventVaultSettings: IEventVaultSettingsUpdateRequest = {
    sharePixWithGuests: settings.shareGalleryWithGuests,
    allowGuestDownload: settings.allowGuestDownload,
    disableGuestUpload: !settings.allowGuestUpload,
    streamingAllowed: settings.allowStreamingVids,
    hideMainGallery: settings.hideMainGallery,
    hideGuestbook: !settings.enableGuestbook,
    allowVideoGuestbook: settings.allowGuestbookVideo,
    shareGuestbookWithGuests: settings.shareGuestbookWithGuests,
    hideGuestEmail: settings.hideGuestEmail,
    allowGuestZipDownload: settings.allowGuestZipDownload,
    invitationEnabled: settings.enableInvitation,
    moderatePhotosAndVideos: settings.moderatePhotosAndVideos,
    moderateMessagesAndComments: settings.moderateMessagesAndComments,
    experienceType: settings.experienceType,
    socialsBehavior: getUpdatedSocialsBehavior(settings.enableReactions, settings.enableComments),
  }

  if (settings?.enableTags !== undefined) {
    updatedEventVaultSettings.taggingBehavior = settings.enableTags ? TaggingBehavior.On : TaggingBehavior.Off
  }

  if (settings?.enableEventWebsite !== undefined) {
    updatedEventVaultSettings.eventWebsiteUrlMode = settings.enableEventWebsite ? EventWebsiteUrlMode.PathBased : EventWebsiteUrlMode.Off
  }

  return axios.put(`api/event/${eventVaultId}/settings`, updatedEventVaultSettings)
}

export async function updateEventVaultInvitationSettings(eventVault: EventVault, enableInvitation: boolean) {
  const updatedEventVaultSettings: Partial<EventVaultSettings> = {
    ...eventVault.eventVaultSettings,
    invitationEnabled: enableInvitation,
  }

  return axios.put(`api/event/${eventVault.id}/settings`, updatedEventVaultSettings)
}

const UPDATE_INVITATION_SETTINGS = "updateInvitationSettings"

export function useUpdateInvitationSettings() {
  return useMutation({
    mutationKey: [UPDATE_INVITATION_SETTINGS],
    mutationFn: ({ eventVault, enableInvitation }: { eventVault: EventVault, enableInvitation: boolean }) => updateEventVaultInvitationSettings(eventVault, enableInvitation),
  })
}

export function getUpdatedSocialsBehavior(enableReactions?: boolean, enableComments?: boolean): SocialsBehavior {
  if (enableReactions === undefined && enableComments === undefined)
    return SocialsBehavior.Disabled
  if (enableReactions && enableComments)
    return SocialsBehavior.ReactionsAndComments
  if (enableReactions)
    return SocialsBehavior.ReactionsOnly
  if (enableComments)
    return SocialsBehavior.CommentsOnly
  return SocialsBehavior.None
}
